import React, { useState, useContext, useRef, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import { DirectionContext } from "../App";
import API_ENDPOINTS from "../config/api";
import Cookies from "js-cookie";

const ScreenshotSubmission = () => {
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const { navigateTo } = useContext(DirectionContext);
  const fileInputRef = useRef(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadResponse, setUploadResponse] = useState(null);
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState("");
  const [visible, setVisible] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = Cookies.get(
          API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN
        );

        if (!token) {
          throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
        }

        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/clients/me`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error("사용자 정보를 가져오는데 실패했습니다.");
        }

        const data = await response.json();
        setUserData(data);
      } catch (err) {
        setError(err.message || "사용자 정보를 가져오는데 실패했습니다.");
      } finally {
        setVisible(true);
      }
    };

    fetchUserData();
  }, []);
  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  const modalVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  };

  // 이미지 확대 보기
  const handleImageEnlarge = (imageSrc) => {
    setEnlargedImage(imageSrc);
  };

  // 확대 이미지 닫기
  const handleCloseEnlarged = () => {
    setEnlargedImage(null);
  };

  // 파일 선택 핸들러
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // 기존 업로드된 이미지가 있다면 메모리에서 해제
      if (uploadedFile && uploadedFile.preview) {
        URL.revokeObjectURL(uploadedFile.preview);
      }

      setUploadedFile({
        file,
        preview: URL.createObjectURL(file),
      });

      // 이미지 선택 시 에러 메시지와 성공 상태 초기화
      setUploadError("");
      setUploadSuccess(false);
      setUploadResponse(null);
    }
  };

  // 파일 업로드 버튼 클릭
  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  // 파일 삭제
  const handleRemoveFile = () => {
    if (uploadedFile && uploadedFile.preview) {
      URL.revokeObjectURL(uploadedFile.preview);
    }
    setUploadedFile(null);
    setUploadSuccess(false);
    setUploadResponse(null);
  };

  // 서버에 스크린샷 제출
  const handleSubmitScreenshot = async () => {
    if (!uploadedFile) return;

    setIsUploading(true);
    setUploadError("");

    try {
      // 토큰 가져오기
      const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);

      if (!token) {
        throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
      }

      // 전화번호 가져오기
      const phoneNumber = Cookies.get("verifiedPhoneNumber");

      if (!phoneNumber) {
        throw new Error("전화번호 정보가 없습니다. 다시 로그인해주세요.");
      }

      // FormData 생성
      const formData = new FormData();
      formData.append("file", uploadedFile.file);

      // API 호출
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/uploads/skt-screenshot?phoneNumber=${phoneNumber}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (response.status !== 201) {
        const errorData = await response.json();
        throw new Error(errorData.message || "스크린샷 업로드에 실패했습니다.");
      }

      // 성공 시 응답 데이터 저장
      const responseData = await response.json();

      // fileKey 값을 쿠키에 저장
      if (responseData.fileKey) {
        Cookies.set("screenshot_fileKey", responseData.fileKey, {
          expires: API_ENDPOINTS.COOKIE_CONFIG.EXPIRES.ACCESS_TOKEN,
          ...API_ENDPOINTS.COOKIE_CONFIG.OPTIONS,
        });

        // 디버깅 메시지
      }

      setUploadResponse(responseData);
      setUploadSuccess(true);
      navigateTo("/news-proof");
    } catch (error) {
      setUploadError(error.message || "파일 업로드 중 오류가 발생했습니다.");
    } finally {
      setIsUploading(false);
    }
  };

  // 다음 페이지로 이동
  const handleNext = () => {
    // 파일이 있고 업로드가 완료되었거나 성공했을 때만 다음으로 이동
    if (uploadedFile) {
      if (!uploadSuccess) {
        // 업로드가 아직 안 됐으면 먼저 업로드 실행
        handleSubmitScreenshot();
      } else {
        // 업로드 성공했으면 다음 페이지로 이동
        navigateTo("/news-proof");
      }
    }
  };

  // 체크박스 토글 핸들러
  const toggleAgreement = () => {
    setAgreementChecked(!agreementChecked);
  };

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          <div>
            <Title>3. 가입자 인증</Title>
          </div>
          <Header>
            <StepIndicator>3 / 4</StepIndicator>
            {userData && userData.telecom === "OTHER" ? (
              <>
                <SubTitle>SKT 해지 인증 자료 업로드</SubTitle>
                <Description>
                  해지 신청 문자 캡처로 과거 가업 내역을 인증하세요
                </Description>
                <InfoMessage>
                  <InfoIcon>ⓘ</InfoIcon>
                  <InfoTextContainer>
                    <InfoTextBold>
                      본 신청은{" "}
                      <HighlightedText>
                        4/18일 이후 해지 및 변경한
                      </HighlightedText>{" "}
                      가입자 대상입니다.
                    </InfoTextBold>
                  </InfoTextContainer>
                </InfoMessage>
              </>
            ) : (
              <>
                <SubTitle>가입자 인증 자료 업로드</SubTitle>
                <Description>
                  핸드폰 화면 캡처로 가입 증빙을 인증하세요
                </Description>
              </>
            )}
          </Header>

          <UploadContainer>
            {uploadError && <ErrorAlert>{uploadError}</ErrorAlert>}
            {uploadSuccess && (
              <SuccessAlert>
                스크린샷이 성공적으로 업로드되었습니다!
              </SuccessAlert>
            )}

            <UploadGuide>
              {userData && userData.telecom === "OTHER" ? (
                <GuideTitle>해지 신청 문자 스크린샷을 찍어주세요</GuideTitle>
              ) : (
                <GuideTitle>해당 화면과 같은 스크린샷을 찍어주세요</GuideTitle>
              )}
              <GuideDescription>
                클릭 시 큰 화면으로 확인할 수 있습니다
              </GuideDescription>
              <ExampleImagesContainer>
                {userData && userData.telecom === "OTHER" ? (
                  <ExampleImage
                    src="/png/otherEx.png"
                    alt="해지 신청 예시 이미지"
                    onClick={() => handleImageEnlarge("/png/otherEx.png")}
                  />
                ) : (
                  <>
                    <ExampleImage
                      src="/png/androidEx.png"
                      alt="안드로이드 예시 이미지"
                      onClick={() => handleImageEnlarge("/png/androidEx.png")}
                    />
                    <ExampleImage
                      src="/png/iphoneEx.png"
                      alt="아이폰 예시 이미지"
                      onClick={() => handleImageEnlarge("/png/iphoneEx.png")}
                    />
                  </>
                )}
              </ExampleImagesContainer>
            </UploadGuide>

            {userData && userData.telecom !== "OTHER" && (
              <OsGuideContainer>
                <OsGuideTitle>안드로이드</OsGuideTitle>
                <OsGuideItem>
                  <OsIcon src="/png/android.png" alt="안드로이드 아이콘" />
                  <OsGuideText>
                    [설정] ＞ [휴대폰 정보] ＞ [상태] ＞ [SIM 카드 상태] 화면
                    캡처
                  </OsGuideText>
                </OsGuideItem>

                <OsGuideTitle>아이폰</OsGuideTitle>
                <OsGuideItem>
                  <OsIcon src="/png/ios.png" alt="iOS 아이콘" />
                  <OsGuideText>
                    [설정] ＞ [셀룰러] ＞ [SIMs] 화면 캡처
                  </OsGuideText>
                </OsGuideItem>
              </OsGuideContainer>
            )}

            {uploadedFile && (
              <UploadedImageContainer>
                <UploadedImageWrapper>
                  <UploadedImage
                    src={uploadedFile.preview}
                    alt="업로드된 이미지"
                  />
                  <RemoveButton onClick={handleRemoveFile}>×</RemoveButton>
                </UploadedImageWrapper>
              </UploadedImageContainer>
            )}

            <HiddenFileInput
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*"
            />
          </UploadContainer>

          {/* 유의사항 및 체크박스 영역 */}
          <NoticeContainer>
            <NoticeTitle>유의사항</NoticeTitle>
            <NoticeList>
              <NoticeItem>
                • SK텔레콤에 가입한 적 없는 없는 경우 소송참여가 불가합니다
              </NoticeItem>
              <NoticeItem>
                • 허위 정보로 참여시 수임비용은 환불되지 않으며,
              </NoticeItem>
              <NoticeItem>
                &nbsp;&nbsp;추후 가입 증빙 서류 제출을 요청드릴 수 있습니다.
              </NoticeItem>
              <NoticeItem>
                &nbsp;&nbsp;(요금 명세서,가입내역 캡쳐 등)
              </NoticeItem>
            </NoticeList>
            <AgreementItem onClick={toggleAgreement}>
              <CheckboxImage
                src={
                  agreementChecked
                    ? "/png/checked.png"
                    : "/png/checkedinActive.png"
                }
                alt="체크박스"
              />
              <AgreementText>
                상기 내용을 모두 확인하였으며 동의합니다.
              </AgreementText>
            </AgreementItem>
          </NoticeContainer>

          <ButtonsContainer>
            <UploadButton
              onClick={handleUploadClick}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={!agreementChecked}
            >
              스크린샷 업로드
            </UploadButton>
            <NextButton
              onClick={handleNext}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={!uploadedFile || isUploading || !agreementChecked}
            >
              {isUploading
                ? "업로드 중..."
                : uploadSuccess
                ? "다음으로"
                : "업로드 및 다음으로"}
            </NextButton>
          </ButtonsContainer>
        </Content>
      </Container>

      {/* 확대된 이미지 모달 */}
      <AnimatePresence>
        {enlargedImage && (
          <EnlargedImageModal
            variants={modalVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            onClick={handleCloseEnlarged}
          >
            <EnlargedImageWrapper onClick={(e) => e.stopPropagation()}>
              <EnlargedImage src={enlargedImage} alt="확대된 이미지" />
            </EnlargedImageWrapper>
          </EnlargedImageModal>
        )}
      </AnimatePresence>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f8f8f8;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #fff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f8f8f8;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);

    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 30px 20px 40px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const Header = styled.div`
  width: 100%;
  margin-bottom: 20px;
  color: #333;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StepIndicator = styled.div`
  display: flex;
  width: 50px;
  height: 29px;
  padding: 10px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 8px;
  background: #0075ff;
  color: white;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 15px;
  margin-top: 5px;
  align-self: flex-start;
`;

const Title = styled.h1`
  color: #00285b;
  text-align: center;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  letter-spacing: -0.34px;
  margin: 5px 0;
  padding-top: 10px;
  word-break: keep-all;
`;

const SubTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 10px;
  word-break: keep-all;
  color: #333;
  text-align: left;
  width: 100%;
`;

const Description = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.4;
`;

const UploadContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
  border-radius: 12px;
  overflow: hidden;
`;

const UploadGuide = styled.div`
  width: 100%;
  padding: 20px;
  background-color: white;
  border: 1px solid rgba(0, 117, 255, 0.3);
  border-radius: 12px;
  margin-bottom: 20px;
`;

const GuideTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 5px;
`;

const GuideDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 15px;
`;

const ExampleImagesContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
`;

const ExampleImage = styled.img`
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;

  &:hover {
    transform: scale(1.05);
  }
`;

const UploadedImageContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: 20px;
  margin-top: 20px;
`;

const UploadedImageWrapper = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
`;

const UploadedImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: rgba(255, 77, 79, 1);
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 18px;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const OsGuideContainer = styled.div`
  width: 100%;
  padding: 20px;
  background-color: white;
  border: 1px solid rgba(0, 117, 255, 0.3);
  border-radius: 12px;
`;

const OsGuideTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-top: 10px;
  margin-bottom: 10px;

  &:first-child {
    margin-top: 0;
  }
`;

const OsGuideItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
`;

const OsIcon = styled.img`
  width: 32px;
  height: 32px;
  margin-right: 10px;
`;

const OsGuideText = styled.p`
  font-size: 14px;
  color: #333;
  line-height: 1.4;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 0px;
  padding: 0 0px;
  gap: 10px;

  @media (min-width: 769px) {
    padding: 0;
    background-color: transparent;
  }
`;

const ActionButtonBase = styled(motion.button)`
  display: flex;
  width: 100%;
  max-width: 400px;
  height: 56px;
  padding: 0px 14px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: none;

  @media (min-width: 769px) {
    max-width: 430px;
    margin: 0 auto;
  }
`;

const UploadButton = styled(ActionButtonBase)`
  background: ${(props) => (props.disabled ? "#E5E5E5" : "#0075ff")};
  color: ${(props) => (props.disabled ? "#999" : "white")};
  opacity: ${(props) => (props.disabled ? 0.8 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
`;

const NextButton = styled(ActionButtonBase)`
  background: ${(props) => (props.disabled ? "#E5E5E5" : "#0075ff")};
  color: ${(props) => (props.disabled ? "#999" : "white")};
  opacity: ${(props) => (props.disabled ? 0.8 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
`;

// 확대 이미지 모달 관련 스타일
const EnlargedImageModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const EnlargedImageWrapper = styled.div`
  position: relative;
  max-width: 90%;
  max-height: 90%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
`;

const EnlargedImage = styled.img`
  display: block;
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
`;

const CloseModalButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  cursor: pointer;
  border: none;
  z-index: 1001;
`;

// 추가 스타일
const ErrorAlert = styled.div`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  background-color: rgba(255, 76, 76, 0.1);
  color: #ff4c4c;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
`;

const SuccessAlert = styled.div`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  background-color: rgba(0, 200, 83, 0.1);
  color: #00c853;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

// InfoMessage와 관련된 스타일 컴포넌트 추가
const InfoMessage = styled.div`
  display: flex;
  align-items: center;
  background-color: rgba(0, 102, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
  width: 100%;
  margin-top: 10px;
  align-self: flex-start;
`;

const InfoIcon = styled.span`
  color: #0075ff;
  margin-top: 2px;
  font-size: 16px;
  margin-right: 10px;
  font-weight: bold;
`;

const InfoTextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoTextBold = styled.span`
  color: #4b5563;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
`;

const HighlightedText = styled.span`
  color: #dc4b3f;
  font-weight: 800;
`;

// 유의사항 스타일 컴포넌트
const NoticeContainer = styled.div`
  width: 100%;
  background-color: white;
  border: 1px solid rgba(0, 117, 255, 0.3);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
`;

const NoticeTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
`;

const NoticeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const NoticeItem = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.4;
`;

const AgreementItem = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-top: 8px;
`;

const CheckboxImage = styled.img`
  width: 20px;
  height: 20px;
  margin-right: 8px;
`;

const AgreementText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

export default ScreenshotSubmission;
